import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useClients } from "@/contexts/ClientContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { UploadCloud, Trash2, FileSpreadsheet, Globe2, Search, X, CalendarRange, Eye } from "lucide-react";
import WorklogHistory from "@/components/WorklogHistory";
import PaginationControl, { usePagination } from "@/components/Pagination";
import UploadRowsDialog from "@/components/UploadRowsDialog";
import MissingUploadsAudit from "@/components/uploads/MissingUploadsAudit";

export default function Uploads({ worklogsOnly = false }) {
  const { selected } = useClients();
  const [platforms, setPlatforms] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [busy, setBusy] = useState(false);
  const [platform, setPlatform] = useState(worklogsOnly ? "worklogs" : "");
  const [period, setPeriod] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [businessUnitId, setBusinessUnitId] = useState("");
  const [file, setFile] = useState(null);

  useEffect(() => {
    api.get("/platforms").then((r) => {
      const list = worklogsOnly
        ? r.data.filter((p) => p.key === "worklogs")
        : r.data.filter((p) => p.key !== "worklogs");
      setPlatforms(list);
      if (worklogsOnly) setPlatform("worklogs");
    });
  }, [worklogsOnly]);

  useEffect(() => {
    if (!selected) return;
    refresh();
    api.get(`/clients/${selected.client_id}/business-units`)
      .then((r) => setBusinessUnits(r.data))
      .catch(() => setBusinessUnits([]));
    setBusinessUnitId("");
    // `refresh` intentionally omitted — it closes over the same state we
    // depend on (selected.client_id, worklogsOnly) and re-running on every
    // re-render would cause an upload list flicker.
  }, [selected?.client_id, worklogsOnly]);

  const refresh = async () => {
    if (!selected) return;
    const { data } = await api.get(`/clients/${selected.client_id}/uploads`);
    const filtered = worklogsOnly
      ? data.filter((u) => u.platform === "worklogs")
      : data.filter((u) => u.platform !== "worklogs");
    setUploads(filtered);
  };

  const selectedBu = useMemo(
    () => businessUnits.find((b) => b.business_unit_id === businessUnitId),
    [businessUnits, businessUnitId]
  );

  // Platforms available for the selected property (filtered by active_platforms).
  // Worklogs are always available regardless of channel selection because they
  // are activity logs that apply to every property.
  const availablePlatforms = useMemo(() => {
    if (worklogsOnly) return platforms.filter((p) => p.key === "worklogs");
    if (!selectedBu) return [];
    const active = new Set(selectedBu.active_platforms || []);
    return platforms.filter((p) => active.has(p.key));
  }, [platforms, selectedBu, worklogsOnly]);

  const selectedPlatform = useMemo(
    () => platforms.find((p) => p.key === platform),
    [platforms, platform]
  );
  const requiresBu = true; // Every upload now belongs to a property
  const supportsHistorical = !!selectedPlatform?.site_specific;

  // Reset platform if it's no longer in the BU's active list (e.g. user switched BU)
  useEffect(() => {
    if (worklogsOnly) return;
    if (platform && selectedBu && !(selectedBu.active_platforms || []).includes(platform)) {
      setPlatform("");
    }
  }, [businessUnitId, worklogsOnly, platform, selectedBu]);

  const submit = async (e) => {
    e.preventDefault();
    if (!file || !platform || !businessUnitId || !period) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("platform", platform);
      fd.append("business_unit_id", businessUnitId);
      fd.append("period_month", period);
      await api.post(`/clients/${selected.client_id}/uploads`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("File imported");
      setFile(null);
      e.target.reset();
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/uploads/${id}`);
      refresh();
      toast.success("Removed");
    } catch { toast.error("Failed"); }
  };

  const buById = useMemo(() => {
    const m = {};
    businessUnits.forEach((b) => { m[b.business_unit_id] = b; });
    return m;
  }, [businessUnits]);

  // ── Search & filters ──────────────────────────────────────────────────────
  const [searchText, setSearchText] = useState("");
  const [filterPlatform, setFilterPlatform] = useState("");
  const [filterBuId, setFilterBuId] = useState("");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [showMissing, setShowMissing] = useState(false);
  const [viewerUploadId, setViewerUploadId] = useState(null);

  const platformLabel = useCallback((key) => {
    const p = platforms.find((x) => x.key === key);
    return p?.label || key;
  }, [platforms]);

  const filteredUploads = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    return uploads.filter((u) => {
      if (filterPlatform && u.platform !== filterPlatform) return false;
      if (filterBuId && u.business_unit_id !== filterBuId) return false;
      if (filterFrom && (u.period_month || "") < filterFrom) return false;
      if (filterTo && (u.period_month || "") > filterTo) return false;
      if (!q) return true;
      const bu = u.business_unit_id ? buById[u.business_unit_id] : null;
      const haystack = [
        u.filename || "",
        u.platform || "",
        platformLabel(u.platform),
        u.period_month || "",
        bu?.name || "",
        bu?.website_url || "",
      ].join(" ").toLowerCase();
      return haystack.includes(q);
    });
  }, [uploads, searchText, filterPlatform, filterBuId, filterFrom, filterTo, buById, platformLabel]);

  const anyFilterActive = Boolean(
    searchText || filterPlatform || filterBuId || filterFrom || filterTo,
  );
  const clearFilters = () => {
    setSearchText("");
    setFilterPlatform("");
    setFilterBuId("");
    setFilterFrom("");
    setFilterTo("");
  };

  // ── Missing uploads audit ─────────────────────────────────────────────────
  // For each property × active platform combo, list the months in the last 12
  // months (including the current month) that DON'T have an upload yet. This
  // makes it obvious what's still pending each month.
  const missingAudit = useMemo(() => {
    if (worklogsOnly || businessUnits.length === 0) return { rows: [], totalMissing: 0 };
    // Build set of "buId|platform|YYYY-MM"
    const have = new Set();
    uploads.forEach((u) => {
      if (!u.business_unit_id) return;
      have.add(`${u.business_unit_id}|${u.platform}|${u.period_month}`);
    });
    // Build last 12 *completed* months descending — the current month is still
    // in progress so we skip it (uploads only arrive at month-end).
    const months = [];
    const now = new Date();
    for (let i = 1; i <= 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
    }
    const rows = [];
    let totalMissing = 0;
    businessUnits.forEach((bu) => {
      const buPlatforms = (bu.active_platforms || []).filter((p) => p !== "worklogs");
      buPlatforms.forEach((plat) => {
        const missing = months.filter((m) => !have.has(`${bu.business_unit_id}|${plat}|${m}`));
        if (missing.length === 0) return;
        totalMissing += missing.length;
        rows.push({
          business_unit_id: bu.business_unit_id,
          bu_name: bu.name,
          platform: plat,
          platform_label: platformLabel(plat),
          missing_months: missing,
        });
      });
    });
    // Sort rows by property name, then platform label
    rows.sort((a, b) =>
      (a.bu_name || "").localeCompare(b.bu_name || "") ||
      (a.platform_label || "").localeCompare(b.platform_label || ""),
    );
    return { rows, totalMissing };
  }, [uploads, businessUnits, worklogsOnly, platformLabel]);

  const pagination = usePagination(filteredUploads);

  if (!selected) return <NoClientHint />;

  return (
    <div className="space-y-8" data-testid={worklogsOnly ? "worklogs-page" : "uploads-page"}>
      <div>
        <div className="label-overline">{worklogsOnly ? "Activities" : "Data Intake"}</div>
        <h1 className="font-display font-extrabold text-4xl tracking-tight">
          {worklogsOnly ? "Worklogs" : "CSV Uploads"}
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          {worklogsOnly
            ? "Upload the team's monthly activity log (CSV or Excel) per property. Columns are auto-detected."
            : "Drop in monthly exports (CSV or Excel) per property. Every upload belongs to a property — and the platforms shown for upload are the channels you've enabled on that property."}
        </p>
      </div>

      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-6">
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
            <div className="space-y-2 md:col-span-1">
              <Label className="label-overline flex items-center gap-1">
                <Globe2 className="h-3 w-3" /> Property <span className="text-red-600 ml-1">*</span>
              </Label>
              <Select
                value={businessUnitId}
                onValueChange={(v) => setBusinessUnitId(v)}
              >
                <SelectTrigger className="rounded-sm h-11" data-testid="upload-bu-select">
                  <SelectValue placeholder={businessUnits.length ? "Select property" : "Add a property first"} />
                </SelectTrigger>
                <SelectContent>
                  {businessUnits.map((b) => (
                    <SelectItem key={b.business_unit_id} value={b.business_unit_id}>
                      P{b.priority || 5} · {b.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {!worklogsOnly && (
              <div className="space-y-2">
                <Label className="label-overline">Platform</Label>
                <Select value={platform} onValueChange={(v) => setPlatform(v)} disabled={!businessUnitId}>
                  <SelectTrigger className="rounded-sm h-11" data-testid="upload-platform-select">
                    <SelectValue placeholder={businessUnitId ? "Select platform" : "Pick a property first"} />
                  </SelectTrigger>
                  <SelectContent>
                    {availablePlatforms.length === 0 && businessUnitId && (
                      <div className="text-xs text-zinc-500 px-2 py-3">
                        No channels enabled for this property.
                        <br />Enable some on the Properties page.
                      </div>
                    )}
                    {availablePlatforms.map((p) => (
                      <SelectItem key={p.key} value={p.key}>
                        {p.label}{p.site_specific ? " *" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label className="label-overline">Reporting month</Label>
              <Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)}
                className="rounded-sm h-11" data-testid="upload-month-input"
                required />
            </div>
            <div className="space-y-2">
              <Label className="label-overline">File (CSV or Excel)</Label>
              <Input type="file" accept=".csv,.xls,.xlsx,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" required
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="rounded-sm h-11" data-testid="upload-file-input" />
            </div>
            <Button type="submit" disabled={busy || !platform || !file || !businessUnitId}
              className="rounded-sm h-11 bg-[#002FA7] hover:bg-[#00227A] text-white"
              data-testid="upload-submit-button">
              <UploadCloud className="h-4 w-4 mr-2" />
              {busy ? "Uploading…" : "Import CSV"}
            </Button>
          </form>
          {supportsHistorical && (
            <p className="text-xs text-zinc-500 mt-3" data-testid="historical-tab-hint">
              💡 For uploading <strong>multi-month historical files</strong> (one row per month —
              e.g. AWStats yearly export), use the dedicated <strong>Historical Stats</strong> tab.
            </p>
          )}
          {!worklogsOnly && businessUnitId && availablePlatforms.length === 0 && (
            <p className="text-xs text-amber-600 mt-3" data-testid="no-platforms-hint">
              This property has no marketing channels enabled yet. Open the
              Properties page and tick the channels you use for it.
            </p>
          )}
          {!worklogsOnly && (
            <p className="text-xs text-zinc-500 mt-3">
              Importing one CSV per month per platform. For wide-format multi-month
              exports (one row per month, e.g. AWStats yearly summary), use the
              dedicated <strong>Historical Stats</strong> tab.
            </p>
          )}
        </CardContent>
      </Card>

      <div>
        {/* Missing uploads audit (only relevant for the platform uploads view) */}
        {!worklogsOnly && (
          <MissingUploadsAudit
            missingAudit={missingAudit}
            showMissing={showMissing}
            setShowMissing={setShowMissing}
            onPrefill={(buId, plat, period) => {
              setBusinessUnitId(buId);
              setPlatform(plat);
              setPeriod(period);
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          />
        )}

        {/* Search + filter toolbar */}
        <Card className="rounded-sm border-zinc-200 mb-4" data-testid="uploads-search-toolbar">
          <CardContent className="p-3">
            <div className="grid grid-cols-1 md:grid-cols-[1fr_180px_220px_140px_140px_auto] gap-2 items-center">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-400" />
                <Input
                  type="text"
                  placeholder="Search filename, platform, property…"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  className="rounded-sm h-9 pl-8 text-sm"
                  data-testid="uploads-search-input"
                />
              </div>
              <Select value={filterPlatform || "__all__"} onValueChange={(v) => setFilterPlatform(v === "__all__" ? "" : v)}>
                <SelectTrigger className="rounded-sm h-9 text-xs" data-testid="uploads-filter-platform">
                  <SelectValue placeholder="All platforms" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All platforms</SelectItem>
                  {platforms.map((p) => (
                    <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterBuId || "__all__"} onValueChange={(v) => setFilterBuId(v === "__all__" ? "" : v)}>
                <SelectTrigger className="rounded-sm h-9 text-xs" data-testid="uploads-filter-property">
                  <SelectValue placeholder="All properties" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All properties</SelectItem>
                  {businessUnits.map((b) => (
                    <SelectItem key={b.business_unit_id} value={b.business_unit_id}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="relative">
                <CalendarRange className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-zinc-400 pointer-events-none" />
                <Input
                  type="month"
                  value={filterFrom}
                  onChange={(e) => setFilterFrom(e.target.value)}
                  className="rounded-sm h-9 pl-7 text-xs"
                  title="From month (YYYY-MM)"
                  data-testid="uploads-filter-from"
                />
              </div>
              <div className="relative">
                <CalendarRange className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-zinc-400 pointer-events-none" />
                <Input
                  type="month"
                  value={filterTo}
                  onChange={(e) => setFilterTo(e.target.value)}
                  className="rounded-sm h-9 pl-7 text-xs"
                  title="To month (YYYY-MM)"
                  data-testid="uploads-filter-to"
                />
              </div>
              <Button
                type="button" variant="ghost" size="sm"
                className="rounded-sm h-9 text-xs whitespace-nowrap"
                disabled={!anyFilterActive}
                onClick={clearFilters}
                data-testid="uploads-clear-filters"
              >
                <X className="h-3.5 w-3.5 mr-1" /> Clear
              </Button>
            </div>
            {anyFilterActive && (
              <div className="text-[11px] text-zinc-500 mt-2 font-mono">
                Showing {filteredUploads.length} of {uploads.length} uploads
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
          <div className="label-overline">
            History · {filteredUploads.length} {filteredUploads.length === 1 ? "upload" : "uploads"}
            {anyFilterActive && uploads.length !== filteredUploads.length && (
              <span className="text-zinc-400 normal-case tracking-normal"> (filtered from {uploads.length})</span>
            )}
          </div>
          <PaginationControl {...pagination} testIdPrefix="uploads-pagination" />
        </div>
        <div className="space-y-1.5">
          {pagination.visible.map((u) => {
            const bu = u.business_unit_id ? buById[u.business_unit_id] : null;
            const ext = (u.filename || "").toLowerCase();
            const isExcel = ext.endsWith(".xls") || ext.endsWith(".xlsx");
            return (
              <Card key={u.upload_id} className="rounded-sm border-zinc-200 hover:border-zinc-300 transition-colors"
                data-testid={`upload-row-${u.upload_id}`}>
                <CardContent className="p-3 flex items-center gap-3">
                  <div
                    className={`h-9 w-9 ${isExcel ? "bg-[#107C41]" : "bg-[#0F9D58]"} text-white rounded-sm flex items-center justify-center flex-shrink-0`}
                    title={isExcel ? "Excel file" : "CSV file"}
                  >
                    <FileSpreadsheet className="h-4 w-4" />
                  </div>
                  <Badge variant="outline" className="rounded-sm text-[10px] uppercase tracking-wider font-mono w-[110px] justify-center flex-shrink-0">
                    {u.platform}
                  </Badge>
                  {bu ? (
                    <Badge className="rounded-sm text-[10px] uppercase tracking-wider bg-zinc-900 text-white max-w-[160px] truncate"
                      title={bu.name}>
                      <Globe2 className="h-2.5 w-2.5 mr-1 flex-shrink-0" />
                      <span className="truncate">{bu.name}</span>
                    </Badge>
                  ) : (
                    <span className="text-[10px] text-zinc-400 uppercase tracking-wider w-[140px]">client-wide</span>
                  )}
                  <span className="font-display font-bold text-sm w-[80px] font-mono">{u.period_month}</span>
                  <span className="text-xs text-zinc-500 flex-1 min-w-0 truncate" title={u.filename}>
                    {u.filename}
                  </span>
                  <span className="text-[11px] text-zinc-500 font-mono whitespace-nowrap">
                    {u.row_count} rows
                  </span>
                  {u.summary?.date_range && (
                    <span className="text-[11px] text-zinc-400 font-mono whitespace-nowrap hidden lg:inline">
                      {u.summary.date_range[0]} → {u.summary.date_range[1]}
                    </span>
                  )}
                  <Button variant="ghost" size="sm" className="rounded-sm flex-shrink-0"
                    onClick={() => setViewerUploadId(u.upload_id)}
                    title="View raw rows in this file"
                    data-testid={`view-upload-${u.upload_id}`}>
                    <Eye className="h-3.5 w-3.5 text-[#002FA7]" />
                  </Button>
                  <Button variant="ghost" size="sm" className="rounded-sm flex-shrink-0"
                    onClick={() => remove(u.upload_id)} data-testid={`delete-upload-${u.upload_id}`}>
                    <Trash2 className="h-3.5 w-3.5 text-red-600" />
                  </Button>
                </CardContent>
              </Card>
            );
          })}
          {filteredUploads.length === 0 && (
            <Card className="rounded-sm border-dashed border-zinc-300 bg-white">
              <CardContent className="p-10 text-center text-sm text-zinc-500">
                {anyFilterActive
                  ? "No uploads match the current filters. Try widening the date range or clearing the search."
                  : "No uploads yet for this client."}
              </CardContent>
            </Card>
          )}
        </div>
        {filteredUploads.length > 0 && (
          <div className="flex justify-end mt-3">
            <PaginationControl {...pagination} testIdPrefix="uploads-pagination-bottom" />
          </div>
        )}
      </div>

      {worklogsOnly && (
        <WorklogHistory clientId={selected.client_id} businessUnits={businessUnits} />
      )}
    </div>
  );
}

function NoClientHint() {
  return (
    <div className="border border-dashed border-zinc-300 rounded-sm bg-white p-10 text-center" data-testid="no-client-hint">
      <div className="label-overline">Select a client</div>
      <p className="text-sm text-zinc-500 mt-2">Use the client picker in the header to choose a client.</p>
    </div>
  );
}
